import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { HERO } from "@/constants/testIds/site";
import HeroDropletsBg from "@/components/site/HeroDropletsBg";

export default function Hero() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Hydrophobic green glass background */}
      <HeroDropletsBg />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-44 md:pt-52 pb-28">
        <div className="max-w-3xl">
          <div
            data-testid={HERO.badge}
            className="fade-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-xs font-semibold tracking-wider uppercase text-emerald-50"
          >
            <Sparkles size={14} className="text-emerald-200" />
            J7 Collective Ltd · ClearShield Applicator · UK
          </div>

          <h1
            data-testid={HERO.heading}
            className="fade-up delay-1 mt-7 font-display font-light text-[40px] sm:text-[56px] md:text-[72px] lg:text-[88px] leading-[0.95] tracking-tight text-white drop-shadow-[0_2px_24px_rgba(0,30,15,0.45)]"
          >
            Glass that stays{" "}
            <span className="italic font-semibold text-emerald-200">
              brilliantly
            </span>{" "}
            clear.
            <span className="block font-bold mt-2">For a decade.</span>
          </h1>

          <p
            data-testid={HERO.subheading}
            className="fade-up delay-2 mt-7 max-w-xl text-lg md:text-xl text-emerald-50 font-medium leading-relaxed drop-shadow-[0_2px_12px_rgba(0,30,15,0.5)]"
          >
            Glass surface protection specialists. On‑site ClearShield
            application by our trained team — for showers, conservatories and
            every kind of glass. Water beads off. Limescale gives up. Cleaning
            becomes effortless — backed by 20 years working with Ritec.
          </p>

          <div className="fade-up delay-3 mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid={HERO.ctaBook}
              onClick={() => scrollTo("booking")}
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white text-emerald-950 font-semibold hover:bg-emerald-50 transition-all shadow-2xl shadow-emerald-950/30"
            >
              Book your application
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid={HERO.ctaLearn}
              onClick={() => scrollTo("how-it-works")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-emerald-950/30 backdrop-blur-md border border-white/25 text-white font-semibold hover:bg-emerald-950/50 transition-all"
            >
              How it works
            </button>
          </div>

          <div className="fade-up delay-4 mt-14 flex flex-wrap items-center gap-6 text-emerald-50">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-200" />
              <span className="text-sm font-medium">10‑year protection</span>
            </div>
            <div className="w-px h-5 bg-white/30 hidden sm:block" />
            <div className="text-sm font-medium">
              <span className="text-emerald-200 font-bold">20 yrs</span> with Ritec
            </div>
            <div className="w-px h-5 bg-white/30 hidden sm:block" />
            <div className="text-sm font-medium">UK on‑site service</div>
          </div>
        </div>
      </div>

      {/* Floating hydrophobic glass card */}
      <div className="hidden lg:block absolute right-12 top-1/3 z-10 fade-up delay-3">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-2 w-80 rotate-2 hover:rotate-0 transition-transform duration-700 shadow-2xl shadow-emerald-950/40 ring-1 ring-white/40">
          <div className="rounded-2xl overflow-hidden zoom-wrap">
            <img
              src="https://images.unsplash.com/photo-1773892085848-5cb03fbb151a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHx3YXRlciUyMGRyb3BsZXRzJTIwY2xlYXIlMjBnbGFzcyUyMG1hY3JvfGVufDB8fHx8MTc4MTM3NTIwOHww&ixlib=rb-4.1.0&q=85"
              alt="Hydrophobic glass — water beading on ClearShield"
              className="w-full h-44 object-cover"
            />
          </div>
          <div className="p-5">
            <div className="text-[10px] font-bold tracking-widest text-emerald-700 uppercase">
              Hydrophobic in action
            </div>
            <div className="mt-2 font-display text-xl font-semibold leading-tight text-emerald-950">
              Water beads. Limescale never sticks.
            </div>
            <div className="mt-2 text-sm text-slate-600 leading-relaxed">
              ClearShield bonds with glass at a molecular level — the surface
              becomes ultra‑smooth and water‑repellent.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
